'use client'

import React, { useState } from 'react'
import Marquee from 'react-fast-marquee'
import { useRouter } from 'next/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFire, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { BarsButton } from '@/components/Buttons'
import Sidebar from './Sidebar'
import { SidebarProvider } from '@/context/SidebarContext'
import { useModal } from '@/hooks/useModal'
import AuthModal from '@/components/Modals/AuthModal'
import { ModalType } from '@/constants/common.contant'
import DateDisplay from '@/components/Common/DateDisplay'
import { useNews } from '@/hooks/useNews'
import Link from 'next/link'
import Image from 'next/image'
import { useAuth } from '@/hooks/useAuth'
import GoogleTranslate from '@/components/Common/GoogleTranslate'
import { AdsMenuResponse, MenuItem } from '@/models/menu.model'
import Navbar from './Navbar'
import Logo from '~/Image/logo.jpg'
import Banner from '~/Image/header-banner.jpg'

export interface HeaderProps {
  menus?: MenuItem[] // ✅ Đánh dấu `menus` là optional
  adsMenus?: AdsMenuResponse[] // ✅ Đánh dấu `adsMenus` là optional
}

const Header = ({ menus = [], adsMenus = [] }: HeaderProps) => {
  const router = useRouter()
  const { openModal } = useModal(ModalType.Auth)
  const { allNews } = useNews()
  const { signout, isLoggedIn, userInfo } = useAuth()

  const [searchQuery, setSearchQuery] = useState('')
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [hidePopupTimeout, setHidePopupTimeout] = useState<NodeJS.Timeout | null>(null)

  // Xử lý khi submit tìm kiếm
  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (searchQuery.trim() !== '') {
      router.push(`/tim-kiem?search_q=${encodeURIComponent(searchQuery)}&cate_code=all`)
    }
  }

  // Xử lý logout
  const handleLogout = async () => {
    try {
      setIsPopupVisible(false) // Đóng popup
      await signout() // Gọi hàm signout từ useAuth
    } catch (error) {
      throw Error('Đăng xuất thất bại:' + error)
    }
  }

  // Hiển thị popup
  const showPopup = () => {
    if (hidePopupTimeout) {
      clearTimeout(hidePopupTimeout) // Hủy timeout nếu đang tồn tại
      setHidePopupTimeout(null)
    }
    setIsPopupVisible(true) // Hiển thị popup
  }

  // Ẩn popup với trì hoãn
  const hidePopup = () => {
    const timeout = setTimeout(() => {
      setIsPopupVisible(false) // Ẩn popup sau khi hết thời gian trì hoãn
    }, 300) // Trì hoãn 300ms (có thể điều chỉnh)
    setHidePopupTimeout(timeout)
  }

  return (
    <header>
      <SidebarProvider>
        <div className="bg-bgDark py-2 text-white fixed left-0 top-0 right-0 z-50 xl:relative">
          <div className="container mx-auto">
            <div className="flex items-center justify-between gap-4">
              {/* Bên trái: Ngày, menu, tin nóng */}
              <div className="flex items-center gap-5 flex-1 min-w-0">
                <DateDisplay className="px-4 py-2 bg-primary rounded-full rounded-tl-none hidden xl:block" />
                <BarsButton />

                {/* Tin nóng */}
                <div className="hidden xl:flex items-center gap-2 flex-1 min-w-0">
                  {/* Label */}
                  <div className="flex items-center whitespace-nowrap font-semibold text-white shrink-0">
                    <FontAwesomeIcon icon={faFire} className="text-[#F86900] mr-1" />
                    TIN NÓNG:
                  </div>
                  {/* Marquee */}
                  <div className="flex-grow overflow-hidden min-w-0">
                    <Marquee gradient={false} speed={50}>
                      {allNews?.map((news, index) => (
                        <Link
                          key={index}
                          href={`/${news.slug}`}
                          className="inline-block text-blue-300 whitespace-nowrap mx-4 hover:underline cursor-pointer"
                        >
                          {news.title}
                        </Link>
                      ))}
                    </Marquee>
                  </div>
                </div>
              </div>

              {/* Bên phải: Search, login, translate */}
              <div className="flex items-center gap-4 shrink-0">
                <form className="relative hidden md:block" onSubmit={handleSearchSubmit}>
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 rounded-full bg-white text-black placeholder-gray-400 w-[220px] focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Tìm kiếm ..."
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </div>
                </form>

                {/* Hiển thị Avatar hoặc nút Đăng nhập */}
                {isLoggedIn ? (
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="relative" onMouseEnter={showPopup} onMouseLeave={hidePopup}>
                      <Image
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                          userInfo?.name || 'User'
                        )}&background=E5E5E5&color=949494&size=40&length=2`}
                        alt="User Avatar"
                        className="w-10 h-10 rounded-full cursor-pointer"
                        width={40}
                        height={40}
                      />
                      {/* Popup menu */}
                      {isPopupVisible && (
                        <div
                          className="absolute top-12 right-0 bg-white text-black shadow-lg rounded-md p-4 w-56 z-50"
                          onMouseEnter={showPopup}
                          onMouseLeave={hidePopup}
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <Image
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                                userInfo?.name || 'User'
                              )}&background=E5E5E5&color=949494&size=40&length=2`}
                              alt="User Avatar"
                              className="w-10 h-10 rounded-full"
                              width={40}
                              height={40}
                            />
                          </div>
                          <hr className="my-2" />
                          <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-200"
                            aria-label="info button"
                          >
                            Thông tin chung
                          </button>
                          <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-200"
                            aria-label="idea button"
                          >
                            Ý kiến của bạn
                          </button>
                          <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-200"
                            aria-label="info save button"
                          >
                            Tin đã lưu
                          </button>
                          <button
                            className="block w-full text-left px-4 py-2 hover:bg-gray-200"
                            aria-label="info seen button"
                          >
                            Tin đã xem
                          </button>
                          <hr className="my-2" />
                          <button
                            onClick={handleLogout}
                            aria-label="exit button"
                            className="block w-full text-left px-4 py-2 text-red-500 hover:bg-gray-200"
                          >
                            Thoát
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Google Translate Dropdown */}
                    <GoogleTranslate />
                  </div>
                ) : (
                  <>
                    <button
                      aria-label="Đăng nhập"
                      onClick={openModal}
                      className="bg-primary py-2 px-4 rounded-sm hover:opacity-90 transition-opacity duration-200"
                    >
                      Đăng nhập
                    </button>
                    <GoogleTranslate />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar và Modal */}
          <Sidebar menus={menus} adsMenus={adsMenus} />
          <AuthModal />
        </div>
      </SidebarProvider>
      <div className="max-w-full bg-primaryBg pt-[56px]">
        <div className="container ">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 py-5">
            {/* Logo */}
            <div className="w-auto">
              <Link href="/">
                <Image
                  src={Logo}
                  alt="logo-tapnews"
                  className="object-cover"
                  width={300}
                  priority
                />
              </Link>
            </div>
            {/* Banner */}
            <div className="w-full md:w-2/3">
              <Image
                src={Banner}
                alt="tiếng nói người Việt toàn cầu"
                className="w-full"
                width={100}
                priority
                unoptimized
              />
            </div>
          </div>
        </div>
        <Navbar menus={menus} adsMenus={adsMenus} />
      </div>
    </header>
  )
}

export default Header
